import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import {
  MatSort,
  MatSortModule,
  Sort,
  SortDirection,
} from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Subject } from 'rxjs';
import { TableColumn } from './table-column';

@Component({
  selector: 'stena-table',
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
  ],
  templateUrl: './stena-table.component.html',
  styleUrls: ['./stena-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class StenaTableComponent<T = unknown>
  implements AfterViewInit, OnDestroy, OnChanges
{
  @Input() data: T[] = [];
  @Input() columns: TableColumn[] = [];

  // Pagination
  @Input() pagination = true;
  @Input() pageSize = 15;
  @Input() totalRecordCount = this.data.length || 0;
  @Input() autoPagination = true;
  @Input() paginatorOptions?: {
    pageSizeOptions: number[];
    pageIndex: number;
    pageSize: number;
    length: number | undefined;
    page: ({ pageIndex, pageSize }: PageEvent) => void;
  };

  @Input() serverSideSorting = false;
  @Input() sortOptions?: {
    active?: string;
    direction?: SortDirection;
    disableClear?: boolean;
    sort: (sort: Sort) => void | Promise<void>;
  };

  // Custom templates
  @Input() templates: { [key: string]: TemplateRef<unknown> } = {};
  @Input() rowClick?: (row: T) => void;
  @Input() rowClass?: (row: T) => string;

  @Input() searchAble = false;

  /** Text shown in a full-width row when the table has no data. */
  @Input() noDataText = 'No data found';

  @Input() showTotal = false;
  @Input() totalColumn = '';

  //Visual customization for the main container
  @Input() minHeight = 0;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  @Output() readonly paging: EventEmitter<PageEvent> = new EventEmitter();

  dataSource = new MatTableDataSource<T>([]);
  destroy: Subject<void> = new Subject();

  mainContainerStyles(): string {
    const baseStyles = `mat-elevation-z8 h-full flex flex-col justify-between overflow-auto`;
    const heightStyle = this.minHeight ? `min-h-[${this.minHeight}px]` : '';
    return `${baseStyles} ${heightStyle}`;
  }

  get displayedColumns(): string[] {
    return this.columns.map((c) => c.columnDef);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      const nextData = (changes['data'].currentValue ?? []) as T[];
      this.dataSource.data = [...nextData];
    }

    if (changes['serverSideSorting'] && this.sort) {
      this.dataSource.sort = this.serverSideSorting ? null : this.sort;
    }
  }

  getNestedProp(obj: any, path: string): any {
    return path.split('.').reduce((acc, key) => acc && acc[key], obj);
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.serverSideSorting ? null : this.sort;
    this.dataSource.sortingDataAccessor = (item: T, property: string) => {
      const row = item as Record<string, unknown>;
      if (property === 'timestamp' && !!row['createdAt']) {
        return new Date(String(row['createdAt'])).getTime();
      } else {
        return this.getNestedProp(item, property) as string | number;
      }
    };
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  trackColumnsBy(index: number, column: TableColumn): string {
    return column.columnDef;
  }

  trackBy(i: number, it: unknown) {
    if (typeof it === 'object' && it !== null && 'id' in it) {
      return (it as { id: unknown }).id;
    }

    return i;
  }

  onPageChange(event: PageEvent): void {
    this.paging.next(event);
  }

  onSortChange(sort: Sort): void {
    if (!this.serverSideSorting || !this.sortOptions) {
      return;
    }

    void this.sortOptions.sort(sort);
  }

  onRowClicked(row: T): void {
    this.rowClick?.(row);
  }

  rowClassValue(row: T): string {
    return this.rowClass?.(row) ?? '';
  }

  isRowClickable(): boolean {
    return !!this.rowClick;
  }

  calculateTotal(): string {
    if (!this.totalColumn) {
      return '';
    }

    return this.data
      .reduce((total, row) => {
        const typedRow = row as { [key: string]: string };
        return total + Number(typedRow[this.totalColumn]);
      }, 0)
      .toFixed(2);
  }
}
